import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, X, Eye, Trash2, CheckCircle, XCircle, User, KeyRound, CreditCard, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import AddDoctor from '../components/modals/AddDoctor';
import PasswordUpdateModal from '../components/modals/PasswordUpdateModal';
import AssignPackageModal from '../components/modals/AssignPackageModal';
import GraceModal from '../components/modals/GraceModal';
import { getAllDoctors, deleteDoctor, updateDoctorStatus } from '../redux/doctorSlice';
import { getDoctorsStatuses } from '../redux/subscriptionSlice';
import { getCountries } from '../redux/masterSlice';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../components/CommonLoader';
import MasterLabel from '../components/common/MasterLabel';
import ErrorPage from '../components/ErrorPage';
import { getFileURL } from '../utils/fileURL';
import { SlidersHorizontal } from 'lucide-react';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../components/common/FilterPanel';

const MySwal = withReactContent(Swal);

export default function Doctor() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(['doctorpage']);
    const userRole = localStorage.getItem('Role');
    const language = localStorage.getItem('i18nextLng');
    const isInitialCall = useRef(true);
    const { doctors, loading, error, total, page, limit, totalPages } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);
    const { doctorsStatuses } = useSelector((state) => state.subscription);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('doctorActiveTab') || 'active'); // 'active' | 'inactive' | 'in-active'
    // Bumped after a doctor is created to force a list refetch even when the tab/page don't change
    const [refreshKey, setRefreshKey] = useState(0);

    // Persist active tab to session storage
    useEffect(() => {
        sessionStorage.setItem('doctorActiveTab', activeTab);
    }, [activeTab]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isAddDoctorOpen, setIsAddDoctorOpen] = useState(false); // Separate modal state
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [planDoctor, setPlanDoctor] = useState(null); // doctor whose subscription package is being assigned
    const [graceDoctor, setGraceDoctor] = useState(null); // expired doctor being granted grace days
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [] });

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Validate search input
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 50) {
            return t('doctorpage.search_too_long', 'Search term cannot exceed 50 characters');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('doctorpage.search_spaces_only', 'Search term cannot contain only spaces');
        }

        return '';
    };

    // Debounce search query (only if no error)
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Only debounce if there's no error
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                // Trim the search query before setting it
                const trimmedSearch = searchQuery.trim();
                setDebouncedSearchQuery(trimmedSearch);
                setCurrentPage(1);
            }, 500);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, searchError]);

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries());
        dispatch(getDoctorsStatuses());
    }, [dispatch]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Refocus search input after loading
    useEffect(() => {
        const active = document.activeElement === inputRef.current;
        if (!active && inputRef.current && searchQuery.length > 0) {
            inputRef.current.focus();
        }
    }, [loading]);

    // Fetch doctors
    useEffect(() => {
        // Don't fetch if there's a search error
        if (searchError) {
            return;
        }

        const params = {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
        };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');

        if (activeTab === 'in-active') {
            // For In-Active tab: use status=inactive, no availability parameter
            params.status = 'inactive';
        } else if (activeTab === 'active') {
            // For Active tab: use availability=true
            params.availability = true;
        } else if (activeTab === 'inactive') {
            // For Inactive tab: use availability=false
            params.availability = false;
        }

        dispatch(getAllDoctors(params));
    }, [currentPage, itemsPerPage, debouncedSearchQuery, activeTab, filters, dispatch, searchError, refreshKey]);

    const handleSubmit = async (formData) => {
        setIsAddDoctorOpen(false);
        // A newly created doctor has no availability set yet, so it lands in the Pending tab
        // (availability=false). Switch there so the created doctor is actually visible.
        if (activeTab !== 'inactive') {
            setActiveTab('inactive');
        }
        setCurrentPage(1);
        // Force a refetch so the newly created doctor shows up instantly, even when
        // we're already on the Pending tab / page 1 (where no state change would otherwise occur).
        setRefreshKey((k) => k + 1);
    };

    const handleSearch = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchQuery so the input stays in sync with what the user types
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
        setCurrentPage(1);
    };

    const handleDeleteDoctor = async (docID) => {
        const result = await MySwal.fire({
            title: t('doctorpage.delete_confirm_title'),
            text: t('doctorpage.delete_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('doctorpage.delete_confirm_button'),
            cancelButtonText: t('doctorpage.cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await dispatch(deleteDoctor(docID)).unwrap();
                    return response;
                } catch (error) {
                    toast.warn(language === 'en' ? error.error?.response?.data?.message?.en : language === 'fr' ? error.error?.response?.data?.message?.fr : t('doctorpage.delete_error_text'));
                    throw error;
                }
            },
            allowOutsideClick: () => !MySwal.isLoading(),
        });

        if (result.isConfirmed) {
            toast.success(t('doctorpage.delete_success_text'));
            const refreshParams = {
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchQuery,
                availability: activeTab === 'active' ? true : false,
            };
            if (filters.gender.length > 0) refreshParams.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) refreshParams.countryId = filters.countryId.join(',');
            dispatch(getAllDoctors(refreshParams));
        }
    };

    const handleStatusToggle = async (doctor) => {
        const currentStatus = doctor.status === 'active';
        const newStatus = !currentStatus; // If active, send false; if inactive, send true
        const actionText = newStatus ? t('doctorpage.activate') || 'activate' : t('doctorpage.deactivate') || 'deactivate';

        // Show confirmation dialog
        const result = await MySwal.fire({
            title: t('doctorpage.status_toggle_confirm_title') || 'Confirm Status Change',
            text: t('doctorpage.status_toggle_confirm_text') || `Are you sure you want to ${actionText} this doctor?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('doctorpage.confirm') || 'Confirm',
            cancelButtonText: t('doctorpage.cancel') || 'Cancel',
        });

        if (!result.isConfirmed) {
            return; // User cancelled
        }

        try {
            await dispatch(updateDoctorStatus({
                doctorId: doctor.doctorId,
                status: newStatus
            })).unwrap();

            // Refresh the doctors list based on current tab
            const params = {
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchQuery,
            };
            if (filters.gender.length > 0) params.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');

            if (activeTab === 'in-active') {
                params.status = 'inactive';
            } else if (activeTab === 'active') {
                params.availability = true;
            } else if (activeTab === 'inactive') {
                params.availability = false;
            }

            dispatch(getAllDoctors(params));

            // Show success message
            toast.success(newStatus
                    ? (t('doctorpage.status_activated') || 'Doctor activated successfully')
                    : (t('doctorpage.status_deactivated') || 'Doctor deactivated successfully'));
        } catch (error) {
            toast.warn(language === 'en'
                    ? error?.error?.response?.data?.message?.en || error?.error?.message || t('doctorpage.status_update_error_text') || 'Failed to update doctor status'
                    : language === 'fr'
                        ? error?.error?.response?.data?.message?.fr || error?.error?.message || t('doctorpage.status_update_error_text') || 'Échec de la mise à jour du statut du médecin'
                        : t('doctorpage.status_update_error_text') || 'Failed to update doctor status');
        }
    };

    // Always map doctors to include fullName property
    const doctorsWithFullName = doctors.map((doctor) => ({
        ...doctor,
        fullName: `${doctor.name || ''} ${doctor.surname || ''}`.trim()
    }));

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const currentDoctors = doctorsWithFullName;
    const totalItems = total;
    const totalPagesCount = totalPages;
    const indexOfFirstDoctor = (currentPage - 1) * itemsPerPage;

    // Handle error state
    if (error) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('doctorpage.all_doctors', 'Doctor Page')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('doctorpage.all_doctors', 'Doctor Page')} />;
        }
        return <ErrorPage error={error} pageName={t('doctorpage.all_doctors', 'Doctor Page')} />;
    }

    return (
        <div data-testid="doctor-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="doctor-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="doctor-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('doctorpage.all_doctors')}
                </h1>
                {userRole === 'admin' && (
                    <button
                        data-testid="add-doctor-button"
                        onClick={() => setIsAddDoctorOpen(true)}  // Fixed: Open AddDoctor modal
                        disabled={loading}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Plus data-testid="add-doctor-icon" className="w-5 h-5" />
                        <span data-testid="add-doctor-text">{t('doctorpage.add_doctor')}</span>
                    </button>
                )}
            </div>

            {/* Tabs */}



            {/* Search + Filter Row */}
            <div data-testid="search-container" className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    <button
                        data-testid="doctor-tab-inactive"
                        onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'inactive' && (
                            <motion.div
                                layoutId="activeDoctorStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('doctorpage.inactive')}</span>
                    </button>

                    <button
                        data-testid="doctor-tab-active"
                        onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'active' && (
                            <motion.div
                                layoutId="activeDoctorStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('doctorpage.active')}</span>
                    </button>

                    <button
                        data-testid="doctor-tab-in-active"
                        onClick={() => { setActiveTab('in-active'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'in-active'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'in-active' && (
                            <motion.div
                                layoutId="activeDoctorStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('doctorpage.disabled') || 'Disabled'}</span>
                    </button>
                </div>
                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        type="text"
                        placeholder={t("doctorpage.search_placeholder")}
                        value={searchQuery}
                        onChange={handleSearch}
                        disabled={false}
                        maxLength={50}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError
                            ? 'border-red-500 focus:ring-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                            } ${loading ? "opacity-70" : ""}`}
                    />
                    <Search data-testid="search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
                    {searchError && (
                        <p data-testid="search-error" className="text-red-600 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
                    )}
                </div>

                {/* Filter Button + Popup */}
                <div data-testid="payments-filter-container" className="relative w-full sm:w-auto">
                    <button
                        data-testid="filter-button"
                        ref={filterButtonRef}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="group flex items-center justify-center gap-2 h-full px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        aria-label={t('doctorpage.filter')}
                        aria-expanded={isFilterOpen}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        {/* {t('doctorpage.filter')} */}
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters({ gender: [], countryId: [] });
                                    setFilters({ gender: [], countryId: [] });
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    setFilters(tempFilters);
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                clearLabel={t('doctorpage.clear')}
                                applyLabel={t('doctorpage.apply')}
                            >
                                <FilterSection title={t('doctorpage.gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={[
                                            { value: 'male', label: t('doctorpage.male') },
                                            { value: 'female', label: t('doctorpage.female') },
                                            { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                            { value: 'other', label: t('doctorpage.other') },
                                        ]}
                                    />
                                </FilterSection>

                                <FilterSection title={t('doctorpage.country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                                    <FilterCountryList
                                        multiple
                                        value={tempFilters.countryId}
                                        onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                        countries={countries}
                                        searchPlaceholder={t('doctorpage.search_placeholder', 'Search...')}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.gender.length > 0 || filters.countryId.length > 0) && (
                <div data-testid="applied-filters-container" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span data-testid="applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
                            {t('doctorpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} data-testid={`applied-filter-gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('doctorpage.gender')}:</span> {t(`doctorpage.${g}`)}
                                </span>
                                <button
                                    data-testid={`remove-gender-filter-${g}`}
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} data-testid={`applied-filter-country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('doctorpage.country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    data-testid={`remove-country-filter-${cid}`}
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            data-testid="clear-all-filters-button"
                            onClick={() => {
                                setFilters({ gender: [], countryId: [] });
                                setTempFilters({ gender: [], countryId: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('doctorpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Table for larger screens, Cards for mobile */}
            {loading ? (
                <div data-testid="doctor-loading-container" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span data-testid="doctor-loading-text" className="text-xl text-[#4ABDBD]">{t('doctorpage.loading')}</span>
                </div>
            ) : (
                <>
                    {/* Table for sm and above */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="doctors-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="serial-no-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.serial_no')}</th>
                                    <th data-testid="name-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.full_name')}</th>
                                    <th data-testid="email-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.email')}</th>
                                    <th data-testid="phone-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.phone')}</th>
                                    <th data-testid="license-no-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.license_no')}</th>
                                    <th data-testid="gender-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.gender')}</th>
                                    <th data-testid="status-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.status_label')}</th>
                                    {activeTab !== 'active' && (<th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('doctorpage.status')}</th>)}
                                    <th data-testid="action-header" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('doctorpage.action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="doctors-table-body">
                                {currentDoctors.length > 0 ? (
                                    currentDoctors.map((doctor, index) => {
                                        const serialNumber = indexOfFirstDoctor + index + 1;
                                        // Generate initials from name and surname
                                        const getInitials = () => {
                                            const nameInitial = doctor.name ? doctor.name.charAt(0).toUpperCase() : '';
                                            const surnameInitial = doctor.surname ? doctor.surname.charAt(0).toUpperCase() : '';
                                            return nameInitial + surnameInitial;
                                        };
                                        const initials = getInitials();
                                        return (
                                            <tr key={doctor._id} data-testid={`doctor-row-${doctor.doctorId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                <td data-testid={`doctor-serial-${doctor.doctorId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{serialNumber}</td>
                                                <td data-testid={`doctor-name-${doctor.doctorId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 flex-shrink-0">
                                                            {doctor.image ? (
                                                                <img
                                                                    src={getFileURL(doctor.image)}
                                                                    alt={doctor.fullName || 'Doctor'}
                                                                    className="w-10 h-10 rounded-full object-cover shadow-md"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                        if (fallback) fallback.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`image-fallback ${doctor.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                            >
                                                                {initials ? (
                                                                    <span className="text-white text-sm font-bold uppercase">
                                                                        {initials}
                                                                    </span>
                                                                ) : (
                                                                    <User className="w-5 h-5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span>{doctor.fullName || '-'}</span>
                                                    </div>
                                                </td>
                                                <td data-testid={`doctor-email-${doctor.doctorId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{doctor.email || '-'}</td>
                                                <td data-testid={`doctor-phone-${doctor.doctorId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{doctor.phone || '-'}</td>
                                                <td data-testid={`doctor-license-${doctor.doctorId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{doctor.licenseNumber || '-'}</td>
                                                <td data-testid={`doctor-gender-${doctor.doctorId}`} className="p-3 text-xs">
                                                    {doctor.gender ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
                                                            {t(`doctorpage.${doctor.gender}`)}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
                                                            -
                                                        </span>
                                                    )}
                                                </td>
                                                <td data-testid={`doctor-status-${doctor.doctorId}`} className="p-3 text-xs">
                                                    <div className="relative">
                                                        <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${doctor.status === 'active'
                                                            ? 'bg-[#4ABDBD]'
                                                            : 'bg-gray-300'
                                                            } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                            onClick={() => !loading && handleStatusToggle(doctor)}
                                                        >
                                                            <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${doctor.status === 'active'
                                                                ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                                : 'translate-x-1'
                                                                }`}></span>
                                                        </div>
                                                    </div>
                                                </td>
                                                {activeTab !== 'active' && (<td className="p-3 text-xs ">
                                                    {doctor.available && doctor.address && doctor.speciality && doctor.profile && doctor.payment !== false ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-green-500/10 to-green-300/10 text-green-700 border border-green-500/20">
                                                            {t('doctorpage.completed')}
                                                        </span>
                                                    ) : (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {!doctor.profile && (<span className="inline-flex items-center  px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                                {t(`doctorpage.profile`)}
                                                            </span>)}
                                                            {!doctor.speciality && (<span className="inline-flex  items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                                {t(`doctorpage.speciality`)}
                                                            </span>)}
                                                            {!doctor.address && (<span className="inline-flex items-center  px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                                {t(`doctorpage.address`)}
                                                            </span>)}
                                                            {!doctor.available && (<span className="inline-flex items-center  px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                                {t(`doctorpage.availability`)}
                                                            </span>)}
                                                            {doctor.payment === false && (<span className="inline-flex items-center  px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                                {t(`doctorpage.payment`, 'Payment & Bank')}
                                                            </span>)}
                                                        </div>
                                                    )}
                                                </td>)}
                                                <td data-testid={`doctor-actions-${doctor.doctorId}`} className="p-3 text-xs flex justify-center gap-2">
                                                    <button
                                                        data-testid={`view-doctor-${doctor.doctorId}`}
                                                        onClick={() => navigate('/doctorProfile', { state: { key: doctor.doctorId, returnTab: activeTab } })}
                                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Eye data-testid={`view-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                    </button>
                                                    {userRole === 'admin' && (
                                                        <>
                                                            <button
                                                                data-testid={`password-doctor-${doctor.doctorId}`}
                                                                onClick={() => {
                                                                    setSelectedDoctorId(doctor.doctorId);
                                                                    setIsPasswordModalOpen(true);
                                                                }}
                                                                className="group/btn text-purple-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <KeyRound data-testid={`password-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                            </button>
                                                            <button
                                                                data-testid={`assign-plan-${doctor.doctorId}`}
                                                                onClick={() => setPlanDoctor(doctor)}
                                                                title="Subscription package"
                                                                className="group/btn text-[#3AA8A8] hover:text-white p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <CreditCard data-testid={`assign-plan-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                            </button>
                                                            {doctorsStatuses[doctor.doctorId]?.status === 'expired' && (
                                                                <button
                                                                    data-testid={`expired-${doctor.doctorId}`}
                                                                    onClick={() => setGraceDoctor(doctor)}
                                                                    title="Subscription expired — grant temporary access"
                                                                    className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden animate-pulse"
                                                                >
                                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                    <AlertTriangle className="h-4 w-4 relative z-10" />
                                                                </button>
                                                            )}
                                                            {/* <button
                                                                data-testid={`delete-doctor-${doctor.doctorId}`}
                                                                onClick={() => handleDeleteDoctor(doctor.doctorId)}
                                                                className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <Trash2 data-testid={`delete-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                            </button> */}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="no-doctors-row-desktop">
                                        <td data-testid="no-doctors-message" colSpan={activeTab === 'active' ? "8" : "9"} className="p-3 text-center text-gray-500">
                                            {t('doctorpage.no_doctors_found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Card layout for mobile screens */}
                    <div className="block sm:hidden space-y-4">
                        {currentDoctors.length > 0 ? (
                            currentDoctors.map((doctor, index) => {
                                const serialNumber = indexOfFirstDoctor + index + 1;
                                // Generate initials from name and surname
                                const getInitials = () => {
                                    const nameInitial = doctor.name ? doctor.name.charAt(0).toUpperCase() : '';
                                    const surnameInitial = doctor.surname ? doctor.surname.charAt(0).toUpperCase() : '';
                                    return nameInitial + surnameInitial;
                                };
                                const initials = getInitials();
                                return (
                                    <div
                                        key={doctor._id}
                                        data-testid={`doctor-card-${doctor.doctorId}`}
                                        className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <span data-testid={`doctor-serial-${doctor.doctorId}`} className="text-xs font-bold text-[#4ABDBD]">
                                                {t('doctorpage.serial_no')}: {serialNumber}
                                            </span>
                                            <div className="flex gap-2">
                                                <button
                                                    data-testid={`view-doctor-${doctor.doctorId}`}
                                                    onClick={() => navigate('/doctorProfile', { state: { key: doctor.doctorId, returnTab: activeTab } })}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye data-testid={`view-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                </button>
                                                {userRole === 'admin' && (
                                                    <>
                                                        <button
                                                            data-testid={`password-doctor-${doctor.doctorId}`}
                                                            onClick={() => {
                                                                setSelectedDoctorId(doctor.doctorId);
                                                                setIsPasswordModalOpen(true);
                                                            }}
                                                            className="group/btn text-purple-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <KeyRound data-testid={`password-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        <button
                                                            data-testid={`assign-plan-mobile-${doctor.doctorId}`}
                                                            onClick={() => setPlanDoctor(doctor)}
                                                            title="Subscription package"
                                                            className="group/btn text-[#3AA8A8] hover:text-white p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <CreditCard className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        {doctorsStatuses[doctor.doctorId]?.status === 'expired' && (
                                                            <button
                                                                data-testid={`expired-mobile-${doctor.doctorId}`}
                                                                onClick={() => setGraceDoctor(doctor)}
                                                                title="Subscription expired — grant temporary access"
                                                                className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden animate-pulse"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <AlertTriangle className="h-4 w-4 relative z-10" />
                                                            </button>
                                                        )}
                                                        {/* <button
                                                            data-testid={`delete-doctor-${doctor.doctorId}`}
                                                            onClick={() => handleDeleteDoctor(doctor.doctorId)}
                                                            className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Trash2 data-testid={`delete-icon-${doctor.doctorId}`} className="h-4 w-4 relative z-10" />
                                                        </button> */}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div data-testid={`doctor-name-${doctor.doctorId}`} className="flex items-center gap-3 text-sm font-semibold text-gray-800">
                                                <div className="relative w-10 h-10 flex-shrink-0">
                                                    {doctor.image ? (
                                                        <img
                                                            src={getFileURL(doctor.image)}
                                                            alt={doctor.fullName || 'Doctor'}
                                                            className="w-10 h-10 rounded-full object-cover shadow-md"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`image-fallback ${doctor.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                    >
                                                        {initials ? (
                                                            <span className="text-white text-sm font-bold uppercase">
                                                                {initials}
                                                            </span>
                                                        ) : (
                                                            <User className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="font-bold">{t('doctorpage.full_name')}:</span> {doctor.fullName || '-'}
                                                </div>
                                            </div>
                                            <div data-testid={`doctor-email-${doctor.doctorId}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('doctorpage.email')}:</span> {doctor.email || '-'}
                                            </div>
                                            <div data-testid={`doctor-phone-${doctor.doctorId}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('doctorpage.phone')}:</span> {doctor.phone || '-'}
                                            </div>
                                            <div data-testid={`doctor-license-${doctor.doctorId}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('doctorpage.license_no')}:</span> {doctor.licenseNumber || '-'}
                                            </div>
                                            <div data-testid={`doctor-gender-${doctor.doctorId}`} className="text-xs">
                                                <span className="font-bold">{t('doctorpage.gender')}:</span>
                                                {doctor.gender ? (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20 ml-1">
                                                        {t(`doctorpage.${doctor.gender}`)}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20 ml-1">
                                                        -
                                                    </span>
                                                )}
                                            </div>
                                            <div data-testid={`doctor-status-${doctor.doctorId}`} className="text-xs flex items-center gap-2">
                                                <span className="font-bold">{t('doctorpage.status')}:</span>
                                                <div className="relative">
                                                    <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${doctor.status === 'active'
                                                        ? 'bg-[#4ABDBD]'
                                                        : 'bg-gray-300'
                                                        } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        onClick={() => !loading && handleStatusToggle(doctor)}
                                                    >
                                                        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${doctor.status === 'active'
                                                            ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                            : 'translate-x-1'
                                                            }`}></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div data-testid="no-doctors-row-mobile" className="p-3 text-center text-gray-500">
                                {t('doctorpage.no_doctors_found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {!loading && totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={currentDoctors.length === 0 ? 'disable' : ''}
                />
            )}

            {/* Add Doctor Modal - Fixed */}
            <AddDoctor
                isOpen={isAddDoctorOpen}
                onClose={() => setIsAddDoctorOpen(false)}
                onSubmit={handleSubmit}
            />

            {/* Password Update Modal */}
            <PasswordUpdateModal
                isOpen={isPasswordModalOpen}
                onClose={() => {
                    setIsPasswordModalOpen(false);
                    setSelectedDoctorId(null);
                }}
                userId={selectedDoctorId}
                userType="doctor"
            />

            {/* Subscription package assignment */}
            {planDoctor && (
                <AssignPackageModal
                    doctor={planDoctor}
                    onClose={() => setPlanDoctor(null)}
                />
            )}

            {/* Grant temporary access to an expired doctor */}
            {graceDoctor && (
                <GraceModal
                    doctor={graceDoctor}
                    onClose={() => setGraceDoctor(null)}
                    onGranted={() => dispatch(getDoctorsStatuses())}
                />
            )}
        </div>
    );
}